import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App, { createDefaultCharacter } from './App';
import CampaignLanding from './CampaignLanding';
import PlayerDashboard from './features/dashboard';
import { useDashboardData } from './features/dashboard/hooks/useDashboardData';
import { usePresenceHeartbeat } from './features/dashboard/hooks/usePresenceHeartbeat';
import { useForgeSubmit } from './features/dashboard/hooks/useForgeSubmit';
import { DevAccessLink, DevDashboardPage, DevReportsPage } from './features/feedback';
import { useFeatureFlags } from './features/feedback';
import { createCharacterSheetUploadClient } from './features/character-import';
import { assertImportWriteSafeValue } from './features/character-import/applyImportPayload';
import { FEATURE_FLAGS } from './storage/featureFlags';
import {
  resolveAuthedViewerContext,
} from './storage/convexViewerContext';
import {
  buildAuthedAppKey,
  buildAuthedInitialLaunch,
  buildCampaignSandboxInitialLaunch,
  buildSyntheticDemoInitialLaunch,
  isLaunchTargetReady,
  isCampaignSandboxLaunchTarget,
  isSyntheticDemoCampaignTarget,
} from './campaignLaunchContext';
import { CAMPAIGN_SANDBOX_SOURCE_CAMPAIGN } from './campaignSandboxContract';
import {
  DEMO_CAMPAIGN_ID,
  normalizeDemoCampaignLaunch,
} from './demoCampaign';
import { buildInitialData, shouldForceLocalMode } from './storage/StorageDriver';
import {
  loadCampaignSandboxState,
  saveCampaignSandboxState,
  resetCampaignSandboxState,
} from './storage/campaignSandboxStorage';
import {
  buildDetachedCampaignSandboxSeed,
  buildDemoCampaignSandboxSeed,
  buildForgePreviewCampaignSandboxSeed,
} from './campaignSandboxSeedBuilders';
import {
  loadCampaignSandboxTemplate,
  saveCampaignSandboxTemplate,
} from './campaignSandboxTemplates';
import {
  buildCampaignSandboxDirectUrl,
  buildCampaignSandboxSeedFromSearch,
} from './campaignSandboxRoute';
import { isCampaignTestCharacter } from './campaignCharacterTargets';
import { getEffectiveCampaignFeatures } from './campaignFeatureAccess';
import {
  installConvexAuthFlagGlobals,
  installTestViewerContextGlobals,
  publishConvexAuthDebugGlobals,
  publishViewerContextDebugGlobal,
  shouldShowAuthRollbackHint,
  TEST_VIEWER_CTX_KEY,
} from './authDebugGlobals';
import { shouldRenderAuthRoot } from './rootMode';
import { isCharacterBuilderRoute } from './features/character-builder';

// Lazy-loaded so the builder bundle isn't pulled into the main app on every
// page load. The route only fires when the pathname matches; the import never
// resolves otherwise. See srd/character-creation-plan.md §6 Phase 1.
const CharacterBuilderLazy = React.lazy(() => import('./features/character-builder'));

// Session 8 (2026-04-19): conditionally wrap in ConvexAuthProvider and gate
// App behind auth. The provider owns its own ConvexReactClient separate from
// the driver's ConvexClient (`src/storage/createConvexDriver.js`). Two
// websockets while Session 8 is isolated; Session 7C (client consolidation)
// may unify. See CONVEX_MIGRATION.md §24.8 + CLAUDE-NOTES-GENERAL.md
// "Option A" note for the long-term plan.
//
// Session 7B (2026-04-20): after authentication, AuthedApp queries
// `authHelpers:myViewerContext` to learn `{viewerKind, viewerCharId}`
// and passes it to App as a prop. useStorage plumbs it into the Convex
// driver so chars + systems subscriptions route through projected queries
// for player viewers.
//
// When `useConvexAuth` is off, App renders as before — demo-auth login path,
// driver continues unauthenticated, no viewerContext. Lazy imports keep
// Convex Auth, the React client, and `convex/react` out of the demo bundle.

// Test-override helper (Session 7B): read a stashed viewerContext from
// sessionStorage if one's present. Set via DevTools
// `__devSetTestViewerContext__({viewerKind, viewerCharId})`. Enables testing
// player-view rendering without actually claiming a char as owner —
// essential for developers who are DMs of everything by default.
const readTestViewerContext = () => {
  try {
    if (typeof window === 'undefined') return null;
    if (process.env.NODE_ENV === 'production') return null;
    const raw = window.sessionStorage.getItem(TEST_VIEWER_CTX_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.viewerKind === 'dm' || parsed.viewerKind === 'player')) {
      return parsed;
    }
    return null;
  } catch (_) {
    return null;
  }
};

// Wraps PlayerDashboard with the Convex bindings + launch-handler glue.
// Tracks selectedCampaignId so useDashboardData can fetch the right roster.
// Exposes the legacy CampaignLanding overlay (recovery / beta access / DM
// invite management) behind an "Account" overflow until Session 11 polishes
// those flows into the dashboard proper.
const CampaignDashboardContainer = ({
  useQuery,
  useMutation,
  useConvex,
  api,
  onEnterApp,
  onSignOut,
  characterImportUploadClient = null,
}) => {
  const [selectedCampaignId, setSelectedCampaignId] = React.useState(null);
  // Dashboard-native character creation (2026-07-14): the "New character"
  // card opens a themed modal instead of launching the legacy select screen.
  // Scratch creates use the general standalone mutation; imports go through
  // the server-revalidated import mutation (Track C), same as the legacy path.
  const createStandaloneCharacterMutation = useMutation(api.campaigns.createStandaloneCharacter);
  const createStandaloneCharacterFromImportMutation = useMutation(api.campaigns.createStandaloneCharacterFromImport);
  const onCreateStandaloneCharacter = React.useCallback(async ({ name, race, charClass, level, maxHp }) => {
    const shell = createDefaultCharacter(name, race, maxHp, '');
    delete shell.password;
    const character = {
      ...shell,
      ...(charClass ? { charClass } : {}),
      ...(Number.isFinite(level) && level > 1 ? { level } : {}),
    };
    await createStandaloneCharacterMutation({
      charId: shell.id,
      data: JSON.stringify(character),
    });
    onEnterApp({ type: 'standaloneChar', charId: shell.id, waitForContext: true });
  }, [createStandaloneCharacterMutation, onEnterApp]);
  const onApplyCharacterImportCreate = React.useCallback(async ({ createArgs, patch, warnings }) => {
    assertImportWriteSafeValue(patch, 'characterImport.patch');
    const importedBase = createDefaultCharacter(createArgs.name, createArgs.race, createArgs.maxHp, '');
    const importedChar = { ...importedBase, ...patch };
    delete importedChar.password;
    const reviewedImportPayload = {
      mode: 'create',
      createArgs,
      patch,
      ...(patch?.importReceipt !== undefined ? { importReceipt: patch.importReceipt } : {}),
      ...(warnings !== undefined ? { warnings } : {}),
    };
    await createStandaloneCharacterFromImportMutation({
      charId: importedBase.id,
      data: JSON.stringify(importedChar),
      payload: JSON.stringify(reviewedImportPayload),
    });
    onEnterApp({ type: 'standaloneChar', charId: importedBase.id, waitForContext: true });
  }, [createStandaloneCharacterFromImportMutation, onEnterApp]);
  const [accountOpen, setAccountOpen] = React.useState(false);
  const [accountInitialCampaignId, setAccountInitialCampaignId] = React.useState(null);
  const [devDashboardOpen, setDevDashboardOpen] = React.useState(false);
  const [includeWorldSummaries, setIncludeWorldSummaries] = React.useState(false);
  const globalFeatureFlags = useFeatureFlags();
  const data = useDashboardData({
    useQuery,
    api,
    selectedCampaignId,
    includeWorldSummaries,
    globalFeatureFlags,
  });

  const setActiveCampaign = useMutation(api.campaigns.setActiveCampaign);
  const clearActiveCampaign = useMutation(api.campaigns.clearActiveCampaign);
  const setActiveCampaignCharacter = useMutation(api.campaigns.setActiveCampaignCharacter);
  const setActiveStandaloneCharacter = useMutation(api.campaigns.setActiveStandaloneCharacter);
  const updateCampaignSettings = useMutation(api.campaigns.updateCampaignSettings);
  const deleteCampaignMutation = useMutation(api.campaigns.deleteCampaign);
  const removeCampaignCharacterMutation = useMutation(api.campaigns.removeCampaignCharacter);
  const deleteStandaloneCharacterMutation = useMutation(api.campaigns.deleteStandaloneCharacter);
  const createAnnouncement = useMutation(api.announcements.createAnnouncement);
  const updateAnnouncement = useMutation(api.announcements.updateAnnouncement);
  const deleteAnnouncement = useMutation(api.announcements.deleteAnnouncement);
  const heartbeat = useMutation(api.presence.heartbeat);
  const createCampaignMutation = useMutation(api.campaigns.createCampaign);
  const createCampaignWithSetupMutation = useMutation(api.campaigns.createCampaignWithSetup);
  const acceptInviteMutation = useMutation(api.campaigns.acceptInvite);
  const createInviteMutation = useMutation(api.campaigns.createInvite);
  const createCampaignCharacterMutation = useMutation(api.campaigns.createCampaignCharacter);
  const updateCampaignTestCharacterPresetsMutation = useMutation(api.campaigns.updateCampaignTestCharacterPresets);
  const applyCampaignWorldSelectionMutation = useMutation(api.worlds.applyCampaignWorldSelection);
  const previewSourceCampaignImportMutation = useMutation(api.worldImports.previewSourceCampaignImport);
  const startSourceCampaignImportMutation = useMutation(api.worldImports.startSourceCampaignImport);
  const promoteLoreSourceToWorldCanon = useMutation(api.worldLore.promoteLoreSourceToWorldCanon);
  const copyLoreSourceToCampaign = useMutation(api.worldLore.copyLoreSourceToCampaign);
  const setNicknameMutation = useMutation(api.userPreferences.setNickname);
  // ConvexReactClient gives us imperative .query() for the preview-invite
  // lookup. The standard useQuery pattern is declarative and tracks a
  // single subscription per arg-set; for an on-demand "preview this code"
  // action, imperative `client.query(...)` is the right shape.
  const convexClient = useConvex();

  // Phase D: keep the user's presence row fresh while the dashboard is open
  // and the tab is visible. Fire-and-forget; the hook handles intervals,
  // tab-visibility, and failure-as-quiet-silence.
  usePresenceHeartbeat({ heartbeat });

  const buildCampaignLaunchMetadata = React.useCallback((campaign) => {
    if (!campaign) return {};
    const metadata = {};
    if (Object.prototype.hasOwnProperty.call(campaign, 'enabledFeatures')) {
      metadata.enabledFeatures = campaign.enabledFeatures || null;
    }
    if (Object.prototype.hasOwnProperty.call(campaign, 'homebrew')) {
      metadata.homebrew = campaign.homebrew || null;
    }
    return metadata;
  }, []);

  const isCampaignModuleEnabled = React.useCallback((campaign, initialView) => {
    const featureKeyByView = {
      combat: 'combat',
      shop: 'shops',
      loot: 'loot',
      crafting: 'crafting',
      travel: 'travelLog',
      systems: 'systems',
      dm: 'systems',
    };
    const featureKey = featureKeyByView[initialView];
    if (!featureKey) return true;
    const effectiveFeatures = getEffectiveCampaignFeatures(campaign?.enabledFeatures || null, globalFeatureFlags);
    return effectiveFeatures[featureKey] !== false;
  }, [globalFeatureFlags]);

  const getCampaignCharacterForLaunch = React.useCallback((campaignId, charId) => (
    (data?.campaignCharacters?.[campaignId] || []).find((ch) => ch.id === charId || ch.charId === charId) || null
  ), [data?.campaignCharacters]);

  const enterAsCampaignChar = React.useCallback(async (campaignId, charId) => {
    const campaign = (data?.campaigns || []).find((cp) => cp.id === campaignId || cp.campaignId === campaignId);
    const campaignRole = campaign?.role;
    const character = getCampaignCharacterForLaunch(campaignId, charId);
    const isCampaignManager = campaignRole === 'owner' || campaignRole === 'dm';
    const isTestCharacter = isCampaignTestCharacter(character);
    const isOwnCharacter = character?.isMine === true || campaign?.myCharacterId === charId || campaign?.yourCharId === charId;
    if (isTestCharacter) {
      if (typeof window !== 'undefined') {
        window.alert('Saved practice characters are legacy rows and cannot be entered live. Use Campaign Sandbox for local testing.');
      }
      return;
    }
    if (isCampaignManager && !isTestCharacter && !isOwnCharacter) {
      if (typeof window !== 'undefined') {
        window.alert('DMs can enter the DM dashboard. Player-owned characters must be entered by their owner.');
      }
      return;
    }
    try {
      await setActiveCampaignCharacter({ campaignId, charId });
    } catch (err) {
      console.error('setActiveCampaignCharacter failed', err);
      return;
    }
    onEnterApp({
      type: 'campaignChar',
      campaignId,
      charId,
      isTestCharacter,
      waitForContext: true,
      ...buildCampaignLaunchMetadata(campaign),
    });
  }, [buildCampaignLaunchMetadata, data?.campaigns, getCampaignCharacterForLaunch, setActiveCampaignCharacter, onEnterApp]);

  const enterAsStandaloneChar = React.useCallback(async (charId) => {
    try {
      await setActiveStandaloneCharacter({ charId });
    } catch (err) {
      console.error('setActiveStandaloneCharacter failed', err);
      return;
    }
    // Standalone launches wait for myViewerContext to actually reflect the
    // standalone selection before mounting App. Mounting early can put the
    // user on the DM dashboard instead of their character's home page
    // because App's auth bridge reacts to whatever viewerContext is when
    // it mounts, even if a refresh is mid-flight. The resolver swap in
    // viewerContextAuth.ts (selection-before-dmChars) is what makes this
    // readiness check actually reach `true` reliably.
    onEnterApp({ type: 'standaloneChar', charId, waitForContext: true });
  }, [setActiveStandaloneCharacter, onEnterApp]);

  const openCreateCharacter = React.useCallback(async () => {
    try {
      await clearActiveCampaign({});
    } catch (err) {
      console.error('clearActiveCampaign failed', err);
      return;
    }
    onEnterApp({
      type: 'standaloneCreate',
      waitForContext: true,
      initialView: 'character-import',
    });
  }, [clearActiveCampaign, onEnterApp]);

  const enterAsCampaignDm = React.useCallback(async (campaignId) => {
    try {
      await setActiveCampaign({ campaignId });
    } catch (err) {
      console.error('setActiveCampaign failed', err);
      return;
    }
    const campaign = (data?.campaigns || []).find((cp) => cp.id === campaignId || cp.campaignId === campaignId);
    onEnterApp({
      type: 'campaignDm',
      campaignId,
      waitForContext: true,
      ...buildCampaignLaunchMetadata(campaign),
    });
  }, [buildCampaignLaunchMetadata, data?.campaigns, setActiveCampaign, onEnterApp]);

  const buildCampaignSandboxSeed = React.useCallback((campaignId, overrides = {}) => {
    const campaign = (data?.campaigns || []).find((cp) => cp.id === campaignId || cp.campaignId === campaignId);
    const characters = data?.campaignSandboxCharacters?.[campaignId]
      || data?.campaignCharacters?.[campaignId]
      || [];
    const requestedPersonaCount = Math.max(0, Math.min(4, Math.floor(Number(overrides.personaCount) || 0)));
    const commonSeedArgs = {
      sandboxId: `campaign-${campaignId}`,
      sourceCampaignId: campaignId,
      name: `${campaign?.name || 'Campaign'} Sandbox`,
      tagline: campaign?.blurb || campaign?.tagline || '',
      baseThemeId: campaign?.baseThemeId || 'fantasy-dark',
      baseVariant: campaign?.baseVariant || 'amethyst',
      rulesMode: campaign?.rulesMode || '5e',
      enabledFeatures: campaign?.enabledFeatures || null,
      homebrew: campaign?.homebrew || null,
      world: {
        worldId: campaign?.worldId || null,
        name: campaign?.worldName || null,
      },
    };
    const template = characters.length === 0
      ? loadCampaignSandboxTemplate(campaignId)
      : null;
    if (characters.length === 0 && (requestedPersonaCount > 0 || template?.personaCount > 0)) {
      return buildDemoCampaignSandboxSeed({
        ...commonSeedArgs,
        sourceMode: CAMPAIGN_SANDBOX_SOURCE_CAMPAIGN,
        personaCount: requestedPersonaCount || template.personaCount,
        presetCategories: overrides.presetCategories || template?.presetCategories || undefined,
        ...overrides,
      });
    }
    return buildDetachedCampaignSandboxSeed({
      ...commonSeedArgs,
      characters,
      ...overrides,
    });
  }, [data?.campaignCharacters, data?.campaignSandboxCharacters, data?.campaigns]);

  const launchCampaignSandboxSeed = React.useCallback((seed) => {
    if (!seed?.launchTarget || !seed?.storageConfig || !seed?.storageSeed) return;
    const initialState = buildInitialData(seed.storageSeed);
    const existingState = loadCampaignSandboxState(seed.storageConfig, null);
    let canResumeFromDirectUrl = !!existingState;
    if (!existingState) {
      canResumeFromDirectUrl = saveCampaignSandboxState(seed.storageConfig, initialState);
    }
    const directUrl = typeof window !== 'undefined'
      ? buildCampaignSandboxDirectUrl(seed, window.location)
      : null;
    if (directUrl && canResumeFromDirectUrl) {
      window.location.assign(directUrl);
      return;
    }
    onEnterApp(seed.launchTarget);
  }, [onEnterApp]);

  const openCampaignSandbox = React.useCallback((campaignId) => {
    launchCampaignSandboxSeed(buildCampaignSandboxSeed(campaignId));
  }, [buildCampaignSandboxSeed, launchCampaignSandboxSeed]);

  const resetCampaignSandbox = React.useCallback((campaignId) => {
    const seed = buildCampaignSandboxSeed(campaignId);
    const resetSucceeded = resetCampaignSandboxState(seed.sandboxId);
    if (!resetSucceeded) {
      onEnterApp(seed.launchTarget);
      return;
    }
    launchCampaignSandboxSeed(seed);
  }, [buildCampaignSandboxSeed, launchCampaignSandboxSeed, onEnterApp]);

  const syncCampaignToSandbox = React.useCallback((campaignId) => {
    const characters = data?.campaignSandboxCharacters?.[campaignId]
      || data?.campaignCharacters?.[campaignId]
      || [];
    const template = loadCampaignSandboxTemplate(campaignId);
    let seedOverrides = {};

    if (characters.length === 0 && !(template?.personaCount > 0)) {
      const shouldAddTestCharacters = typeof window === 'undefined'
        || window.confirm("This campaign has no party members or sandbox test characters yet. Add Li'ka, Ventyl, Eve, and Khan as local sandbox test characters before syncing?");
      if (!shouldAddTestCharacters) return;
      seedOverrides = { personaCount: 4 };
      saveCampaignSandboxTemplate(campaignId, { dummyCount: 4 });
    }

    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Sync the current campaign setup into the local sandbox? This replaces local sandbox state and does not change the live campaign.');
      if (!confirmed) return;
    }

    const seed = buildCampaignSandboxSeed(campaignId, seedOverrides);
    const resetSucceeded = resetCampaignSandboxState(seed.sandboxId);
    if (!resetSucceeded) {
      onEnterApp(seed.launchTarget);
      return;
    }
    launchCampaignSandboxSeed(seed);
  }, [buildCampaignSandboxSeed, data?.campaignCharacters, data?.campaignSandboxCharacters, launchCampaignSandboxSeed, onEnterApp]);

  const saveCampaignSettings = React.useCallback(async (campaignId, patch) => {
    await updateCampaignSettings({ campaignId, ...patch });
  }, [updateCampaignSettings]);

  const onDeleteCampaign = React.useCallback(async (campaignId, confirmName) => {
    await deleteCampaignMutation({ campaignId, confirmName });
    // If the user was viewing this campaign on the dashboard, clear the
    // selection so the home view doesn't try to read its (now-deleted) roster.
    setSelectedCampaignId((prev) => prev === campaignId ? null : prev);
  }, [deleteCampaignMutation]);

  const onRemoveCampaignCharacter = React.useCallback(async (campaignId, charId) => {
    await removeCampaignCharacterMutation({ campaignId, charId });
  }, [removeCampaignCharacterMutation]);

  const onDeleteStandaloneCharacter = React.useCallback(async (charId) => {
    await deleteStandaloneCharacterMutation({ charId });
  }, [deleteStandaloneCharacterMutation]);

  const onUpdateCampaignTestCharacterPresets = React.useCallback(async (campaignId, payload) => {
    await updateCampaignTestCharacterPresetsMutation({
      campaignId,
      testPresetCategories: payload?.testPresetCategories || undefined,
    });
  }, [updateCampaignTestCharacterPresetsMutation]);

  const onCreateAnnouncement = React.useCallback(async (campaignId, patch) => {
    await createAnnouncement({ campaignId, ...patch });
  }, [createAnnouncement]);

  const onUpdateAnnouncement = React.useCallback(async (announcementId, patch) => {
    await updateAnnouncement({ announcementId, ...patch });
  }, [updateAnnouncement]);

  const onDeleteAnnouncement = React.useCallback(async (announcementId) => {
    await deleteAnnouncement({ announcementId });
  }, [deleteAnnouncement]);

  const onWorldSummariesNeedChange = React.useCallback((needed) => {
    setIncludeWorldSummaries(!!needed);
  }, []);

  const openCampaignCharacterSetup = React.useCallback(async (campaignId) => {
    if (!campaignId) return;
    try {
      await setActiveCampaign({ campaignId });
    } catch (err) {
      console.error('setActiveCampaign failed', err);
      return;
    }
    setSelectedCampaignId(campaignId);
    setAccountInitialCampaignId(campaignId);
    setAccountOpen(true);
  }, [setActiveCampaign]);

  // Campaign Forge — chained create flow.
  //   1. createCampaign — minimum required (name); makes the caller owner
   //      and sets it as their active campaign.
  //   2. updateCampaignSettings — persist all the rich Forge fields:
  //      tagline / description / baseThemeId / enabledFeatures / schedule /
  //      sourceCampaignId / templateOrigin / linkedElements.
  //   3. createInvite — if generateInvite was checked, mint a code so the
  //      DM has something to share.
  //   4. createCampaignTestCharacter (per known dummy character) — marked
  //      test rows the DM can use before real players arrive.
  // Each step is independent of the rest; a failure in step 2+ is logged
  // but does not undo the campaign creation. The user can edit settings
  // and generate invites later from Campaign Settings.
  const onCreateCampaign = React.useCallback(async (input) => {
    // Backwards-compatible with the older { name } shape from the previous
    // CreateCampaignModal, but Forge sends the full payload.
    const fields = typeof input === 'string' ? { name: input } : (input || {});
    const trimmedName = (fields.name || '').trim();
    if (!trimmedName) throw new Error('Campaign name is required');

    const result = await createCampaignMutation({ name: trimmedName });
    if (!result?.campaignId) return result;
    const campaignId = result.campaignId;

    // Persist rich metadata. Empty fields are sent through and stripped
    // server-side; the impl treats '' as "clear", undefined as "no change".
    const metadataPatch = {};
    if (fields.tagline !== undefined) metadataPatch.tagline = fields.tagline;
    if (fields.description !== undefined) metadataPatch.description = fields.description;
    if (fields.schedule !== undefined) metadataPatch.schedule = fields.schedule;
    if (fields.baseThemeId !== undefined) metadataPatch.baseThemeId = fields.baseThemeId || '';
    if (fields.enabledFeatures) metadataPatch.enabledFeatures = fields.enabledFeatures;
    if (fields.templateOrigin) metadataPatch.templateOrigin = fields.templateOrigin;
    if (fields.sourceCampaignId) metadataPatch.sourceCampaignId = fields.sourceCampaignId;
    if (fields.originSourceCampaignId) metadataPatch.originSourceCampaignId = fields.originSourceCampaignId;
    if (fields.rulesSourceCampaignId) metadataPatch.rulesSourceCampaignId = fields.rulesSourceCampaignId;
    if (fields.linkedElements) metadataPatch.linkedElements = fields.linkedElements;
    if (Object.keys(metadataPatch).length > 0) {
      try {
        await updateCampaignSettings({ campaignId, ...metadataPatch });
      } catch (err) {
        console.warn('campaign metadata persist failed; campaign was created', err);
      }
    }

    if (fields.generateInvite) {
      try {
        await createInviteMutation({ campaignId });
      } catch (err) {
        console.warn('invite generation failed; create via Campaign Settings later', err);
      }
    }

    return result;
  }, [createCampaignMutation, updateCampaignSettings, createInviteMutation]);

  // CampaignForgeV2 submit hook. Translates the wizard's `form` shape via
  // forgeAdapter, then chains campaign creation, optional world selection,
  // settings, and invite creation. Sandbox personas are preview-only local data.
  const submitCampaignFromForgeV2 = useForgeSubmit({
    createCampaign: createCampaignMutation,
    createCampaignWithSetup: createCampaignWithSetupMutation,
    updateCampaignSettings,
    createInvite: createInviteMutation,
    createCampaignCharacter: createCampaignCharacterMutation,
    setActiveCampaign,
    applyCampaignWorldSelection: applyCampaignWorldSelectionMutation,
    previewSourceCampaignImport: previewSourceCampaignImportMutation,
    startSourceCampaignImport: startSourceCampaignImportMutation,
  });

  const onCreateCampaignFromForgeV2 = React.useCallback(async (form) => {
    try {
      const result = await submitCampaignFromForgeV2(form);
      if (result?.campaignId) {
        saveCampaignSandboxTemplate(result.campaignId, form);
      }
      return result;
    } catch (err) {
      if (err?.partialSuccess && err?.campaignId) {
        saveCampaignSandboxTemplate(err.campaignId, form);
      }
      throw err;
    }
  }, [submitCampaignFromForgeV2]);

  const onPreviewCampaignForgeSandbox = React.useCallback((form) => {
    const sandboxId = 'forge-preview-current';
    const seed = buildForgePreviewCampaignSandboxSeed(form, {
      sandboxId,
    });
    const resetSucceeded = resetCampaignSandboxState(sandboxId);
    if (!resetSucceeded) {
      onEnterApp(seed.launchTarget);
      return;
    }
    launchCampaignSandboxSeed(seed);
  }, [launchCampaignSandboxSeed, onEnterApp]);

  const onSetNickname = React.useCallback(async (nickname) => {
    await setNicknameMutation({ nickname });
  }, [setNicknameMutation]);

  const onPreviewInvite = React.useCallback(async (code) => {
    if (!code) return null;
    return await convexClient.query(api.campaigns.previewInvite, { code });
  }, [convexClient, api.campaigns.previewInvite]);

  const onAcceptInvite = React.useCallback(async (code) => {
    return await acceptInviteMutation({ code });
  }, [acceptInviteMutation]);

  const onCreateCampaignInvite = React.useCallback(async (campaignId) => {
    return await createInviteMutation({ campaignId });
  }, [createInviteMutation]);

  const onPromoteLoreSource = React.useCallback(async ({ campaignId, sourceType, sourceId }) => {
    return await promoteLoreSourceToWorldCanon({
      campaignId,
      sourceType,
      sourceId,
      confirmed: true,
    });
  }, [promoteLoreSourceToWorldCanon]);

  const onCopyLoreSourceToCampaign = React.useCallback(async ({ campaignId, sourceType, sourceId }) => {
    return await copyLoreSourceToCampaign({
      campaignId,
      sourceType,
      sourceId,
    });
  }, [copyLoreSourceToCampaign]);

  const enterDemoCampaign = React.useCallback(({ role, charId } = {}) => {
    const launch = normalizeDemoCampaignLaunch({ role, charId });
    if (!launch.valid) {
      console.warn('Unknown demo campaign character launch ignored', {
        charId: launch.charId,
        reason: launch.reason,
      });
      return;
    }
    onEnterApp({
      type: 'syntheticDemoCampaign',
      campaignId: DEMO_CAMPAIGN_ID,
      role: launch.role,
      charId: launch.charId,
      waitForContext: false,
      storageMode: 'localDemo',
    });
  }, [onEnterApp]);

  // Polish (2026-05-17) — Party Journal launch. Picks the right launchTarget
  // shape based on the user's role in the campaign: DMs enter via campaignDm,
  // players via campaignChar using their active campaign character (if known).
  // Either way App.js sees initialView:'journal' and routes there at boot.
  const openPartyJournal = React.useCallback(async (campaignId) => {
    if (!campaignId) return;
    const campaign = (data?.campaigns || []).find((cp) => cp.id === campaignId);
    const role = campaign?.role;
    if (role === 'owner' || role === 'dm') {
      try { await setActiveCampaign({ campaignId }); }
      catch (err) { console.error('setActiveCampaign failed', err); return; }
      onEnterApp({
        type: 'campaignDm',
        campaignId,
        waitForContext: true,
        initialView: 'journal',
        ...buildCampaignLaunchMetadata(campaign),
      });
      return;
    }
    const mineCharId = campaign?.myCharacterId
      || campaign?.yourCharId
      || (data?.campaignCharacters?.[campaignId] || [])
        .find((ch) => ch.isMine && ch.isPlayable !== false && !isCampaignTestCharacter(ch))?.id;
    if (!mineCharId) {
      if (typeof window !== 'undefined') {
        window.alert('You need a character in this campaign to open the journal. Use Enter on the character first.');
      }
      return;
    }
    try { await setActiveCampaignCharacter({ campaignId, charId: mineCharId }); }
    catch (err) { console.error('setActiveCampaignCharacter failed', err); return; }
    onEnterApp({
      type: 'campaignChar',
      campaignId,
      charId: mineCharId,
      waitForContext: true,
      initialView: 'journal',
      ...buildCampaignLaunchMetadata(campaign),
    });
  }, [buildCampaignLaunchMetadata, data, setActiveCampaign, setActiveCampaignCharacter, onEnterApp]);

  const openCampaignModule = React.useCallback(async (campaignId, initialView) => {
    if (!campaignId || !initialView) return;
    const campaign = (data?.campaigns || []).find((cp) => cp.id === campaignId);
    if (!isCampaignModuleEnabled(campaign, initialView)) {
      if (typeof window !== 'undefined') {
        window.alert('That campaign tool is disabled for this campaign.');
      }
      return;
    }
    const role = campaign?.role;
    if (role === 'owner' || role === 'dm') {
      try { await setActiveCampaign({ campaignId }); }
      catch (err) { console.error('setActiveCampaign failed', err); return; }
      onEnterApp({
        type: 'campaignDm',
        campaignId,
        waitForContext: true,
        initialView,
        ...buildCampaignLaunchMetadata(campaign),
      });
      return;
    }
    const mineCharId = campaign?.myCharacterId
      || campaign?.yourCharId
      || (data?.campaignCharacters?.[campaignId] || [])
        .find((ch) => ch.isMine && ch.isPlayable !== false && !isCampaignTestCharacter(ch))?.id;
    if (!mineCharId) {
      if (typeof window !== 'undefined') {
        window.alert('You need a character in this campaign to open that tool.');
      }
      return;
    }
    try { await setActiveCampaignCharacter({ campaignId, charId: mineCharId }); }
    catch (err) { console.error('setActiveCampaignCharacter failed', err); return; }
    onEnterApp({
      type: 'campaignChar',
      campaignId,
      charId: mineCharId,
      waitForContext: true,
      initialView,
      ...buildCampaignLaunchMetadata(campaign),
    });
  }, [buildCampaignLaunchMetadata, data, isCampaignModuleEnabled, setActiveCampaign, setActiveCampaignCharacter, onEnterApp]);

  return (
    <>
      <PlayerDashboard
        data={data}
        onSelectCampaign={setSelectedCampaignId}
        onWorldSummariesNeedChange={onWorldSummariesNeedChange}
        onEnterAsCampaignChar={enterAsCampaignChar}
        onEnterAsStandaloneChar={enterAsStandaloneChar}
        onEnterAsCampaignDm={enterAsCampaignDm}
        onEnterDemoCampaign={enterDemoCampaign}
        onSaveCampaignSettings={saveCampaignSettings}
        onDeleteCampaign={onDeleteCampaign}
        onRemoveCampaignCharacter={onRemoveCampaignCharacter}
        onDeleteStandaloneCharacter={onDeleteStandaloneCharacter}
        onUpdateCampaignTestCharacterPresets={onUpdateCampaignTestCharacterPresets}
        onCreateAnnouncement={onCreateAnnouncement}
        onUpdateAnnouncement={onUpdateAnnouncement}
        onDeleteAnnouncement={onDeleteAnnouncement}
        onOpenPartyJournal={openPartyJournal}
        onOpenCampaignModule={openCampaignModule}
        onOpenCampaignSandbox={openCampaignSandbox}
        onResetCampaignSandbox={resetCampaignSandbox}
        onSyncCampaignSandbox={syncCampaignToSandbox}
        onOpenCreateCharacter={openCreateCharacter}
        onCreateStandaloneCharacter={onCreateStandaloneCharacter}
        onApplyCharacterImportCreate={onApplyCharacterImportCreate}
        characterImportUploadClient={characterImportUploadClient}
        onOpenCampaignCharacterSetup={openCampaignCharacterSetup}
        onOpenDevDashboard={() => setDevDashboardOpen(true)}
        onCreateCampaign={onCreateCampaign}
        onCreateCampaignFromForgeV2={onCreateCampaignFromForgeV2}
        onPreviewCampaignForgeSandbox={onPreviewCampaignForgeSandbox}
        onCreateCampaignInvite={onCreateCampaignInvite}
        onPreviewInvite={onPreviewInvite}
        onAcceptInvite={onAcceptInvite}
        onPromoteLoreSource={onPromoteLoreSource}
        onCopyLoreSourceToCampaign={onCopyLoreSourceToCampaign}
        onSetNickname={onSetNickname}
        myPreferences={data?.myPreferences}
        onSignOut={onSignOut}
      />
      {/* "More options" overflow — opens the legacy CampaignLanding as a
          full-page overlay for recovery, beta access, and DM invite
          management until Session 11 brings those flows into the
          dashboard's own vocabulary. */}
      <button
        type="button"
        onClick={() => {
          setAccountInitialCampaignId(null);
          setAccountOpen(true);
        }}
        title="Account & recovery (legacy view)"
        style={{
          position: 'fixed',
          bottom: 20,
          left: 20,
          padding: '6px 12px',
          background: 'rgba(0,0,0,0.45)',
          color: '#c9a227',
          border: '1px solid rgba(201,162,39,0.4)',
          borderRadius: 4,
          fontFamily: 'serif',
          fontSize: '0.78rem',
          cursor: 'pointer',
          zIndex: 50,
        }}
      >Account & recovery →</button>
      {accountOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.85)',
          zIndex: 100,
          overflow: 'auto',
        }}>
          <button
            type="button"
            onClick={() => setAccountOpen(false)}
            style={{
              position: 'fixed',
              top: 14,
              right: 18,
              background: 'rgba(0,0,0,0.6)',
              color: '#e8dcc4',
              border: '1px solid rgba(232,220,196,0.3)',
              borderRadius: 4,
              padding: '6px 14px',
              fontFamily: 'serif',
              fontSize: '0.85rem',
              cursor: 'pointer',
              zIndex: 110,
            }}
          >← Back to dashboard</button>
          <CampaignLanding
            useQuery={useQuery}
            useMutation={useMutation}
            api={api}
            initialSelectedCampaignId={accountInitialCampaignId}
            characterImportUploadClient={characterImportUploadClient}
            onEnterApp={(target) => { setAccountOpen(false); onEnterApp(target); }}
            onSignOut={onSignOut}
          />
        </div>
      )}
      {/* Dev dashboard overlay — opened from the DevAccessLink on HomeView.
          DevDashboardPage internally re-checks dev access via useIsDev();
          non-devs that somehow reach this path see its access-denied state. */}
      {devDashboardOpen && (
        <DevDashboardOverlay onClose={() => setDevDashboardOpen(false)} />
      )}
    </>
  );
};

// Small in-place overlay so the DevDashboardPage can navigate to its own
// nested reports view without leaving the dashboard context. Mirrors how
// App.js routes between 'dev-dashboard' and 'dev-reports'.
const DevDashboardOverlay = ({ onClose }) => {
  const [reportsView, setReportsView] = React.useState(false);
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.92)',
      zIndex: 100,
      overflow: 'auto',
    }}>
      {reportsView ? (
        <DevReportsPage onBack={() => setReportsView(false)} />
      ) : (
        <DevDashboardPage
          onBack={onClose}
          onNavigateToReports={() => setReportsView(true)}
        />
      )}
    </div>
  );
};

// Signed-out companion to the maintenance gate: a banner on the sign-in
// screen. The flags query is public, so unauthenticated visitors see the
// notice before bouncing off a sign-in that leads to the full screen.
const SignedOutMaintenanceNotice = ({ useQuery, api }) => {
  const flags = useQuery(api.featureFlags.flags, {});
  if (flags?.['system.maintenance']?.enabled !== true) return null;
  return (
    <div style={{
      margin: '0 auto 24px',
      padding: '10px 14px',
      background: 'rgba(201,162,39,0.12)',
      border: '1px solid rgba(201,162,39,0.45)',
      borderRadius: 6,
      fontSize: '0.9rem',
      lineHeight: 1.5,
    }}>
      🛠 Survival Tracker is currently down for maintenance. Check back soon.
    </div>
  );
};

// Full-page takeover while `system.maintenance` is ON (dev dashboard toggle).
// Dev-access accounts never see this — they pass through to verify fixes.
const MaintenanceScreen = ({ onSignOut }) => (
  <div style={{ textAlign: 'center', padding: '96px 24px', color: '#e8dcc4', fontFamily: 'serif', maxWidth: 520, margin: '0 auto' }}>
    <div style={{ fontSize: '2.6rem', marginBottom: 16 }}>🛠</div>
    <h1 style={{ fontSize: '1.6rem', marginBottom: 12, color: '#c9a227' }}>Down for maintenance</h1>
    <p style={{ lineHeight: 1.6, opacity: 0.85, marginBottom: 32 }}>
      Survival Tracker is briefly offline while we make improvements.
      Your characters and campaigns are safe — check back soon.
    </p>
    {onSignOut && (
      <button
        onClick={onSignOut}
        style={{
          padding: '9px 18px',
          background: 'transparent',
          color: '#b89a70',
          border: '1px solid #5c4a2a',
          borderRadius: 4,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: '0.9rem',
        }}
      >Sign out</button>
    )}
  </div>
);

const AuthedApp = ({ useQuery, useMutation, useAction, useConvex, api, authToken, signOut }) => {
  const serverVc = useQuery(api.authHelpers.myViewerContext, {});
  const claimCharAsOwner = useMutation(api.authHelpers.claimCharAsOwner);
  // Maintenance gate (2026-07-14): when the system.maintenance flag is ON,
  // everyone without dev access gets the maintenance screen instead of the
  // dashboard or the app. Reactive — flipping the flag in the dev dashboard
  // takes effect on connected clients immediately.
  const globalFlags = useQuery(api.featureFlags.flags, {});
  const myDevAccessForGate = useQuery(api.devAccess.myDevAccess, {});
  const maintenanceActive = globalFlags?.['system.maintenance']?.enabled === true;
  const maintenanceBypass = myDevAccessForGate?.hasAccess === true;
  // Import creates go through the server-validated mutation (Track C):
  // re-runs the import boundary checks before storing the character.
  const createStandaloneCharacterFromImport = useMutation(api.campaigns.createStandaloneCharacterFromImport);
  const createCharacterImportUploadSession = useMutation(api.characterImports.createUploadSession);
  const registerCharacterImportUpload = useMutation(api.characterImports.registerUpload);
  const previewUploadedCharacterPdf = useAction(api.characterImportActions.previewUploadedCharacterPdf);
  const testVc = React.useMemo(readTestViewerContext, []);
  const [launchTarget, setLaunchTarget] = React.useState(null);
  const liveCampaignRuntimeSettings = useQuery(
    api.campaigns.getCampaignRuntimeSettings,
    launchTarget?.campaignId && !isSyntheticDemoCampaignTarget(launchTarget)
      ? { campaignId: launchTarget.campaignId }
      : 'skip',
  );

  const viewerContext = React.useMemo(() => resolveAuthedViewerContext({
    serverViewerContext: serverVc,
    testViewerContext: testVc,
  }), [serverVc, testVc]);

  React.useEffect(() => {
    return publishViewerContextDebugGlobal(viewerContext);
  }, [viewerContext]);

  const characterImportUploadClient = React.useMemo(() => createCharacterSheetUploadClient({
    createUploadSession: createCharacterImportUploadSession,
    registerUpload: registerCharacterImportUpload,
    previewUploadedCharacterPdf,
  }), [createCharacterImportUploadSession, registerCharacterImportUpload, previewUploadedCharacterPdf]);

  const onCharacterImportCreateApplied = React.useCallback((charId) => {
    if (!charId) return;
    setLaunchTarget({ type: 'standaloneChar', charId, waitForContext: true });
  }, []);

  if (maintenanceActive && !maintenanceBypass) {
    // Hold a quiet loading state while dev access resolves so admins don't
    // flash the maintenance screen; everyone else sees it immediately.
    if (myDevAccessForGate === undefined) {
      return (
        <div style={{ textAlign: 'center', padding: '80px 24px', color: '#e8dcc4', fontFamily: 'serif' }}>
          <div style={{ fontSize: '1.3rem' }}>Loading…</div>
        </div>
      );
    }
    return <MaintenanceScreen onSignOut={signOut} />;
  }

  if (!testVc && !launchTarget) {
    return (
      <CampaignDashboardContainer
        useQuery={useQuery}
        useMutation={useMutation}
        useConvex={useConvex}
        api={api}
        onEnterApp={setLaunchTarget}
        onSignOut={signOut}
        characterImportUploadClient={characterImportUploadClient}
      />
    );
  }

  if (!isLaunchTargetReady(launchTarget, serverVc)) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px', color: '#e8dcc4', fontFamily: 'serif' }}>
        <div style={{ fontSize: '1.3rem' }}>Opening campaign...</div>
      </div>
    );
  }

  const syntheticDemoLaunch = isSyntheticDemoCampaignTarget(launchTarget);
  const campaignSandboxLaunch = isCampaignSandboxLaunchTarget(launchTarget);
  const localOnlyLaunch = syntheticDemoLaunch || campaignSandboxLaunch;
  const campaignCharLaunch = !localOnlyLaunch && launchTarget?.type === 'campaignChar';
  const shouldSynthesizeCampaignTestCharContext = campaignCharLaunch
    && launchTarget?.isTestCharacter === true
    && serverVc?.viewerKind === 'dm';
  const campaignCharViewerContext = shouldSynthesizeCampaignTestCharContext && viewerContext
    ? {
        ...viewerContext,
        viewerKind: 'player',
        viewerCharId: launchTarget.charId,
        activeCampaignId: launchTarget.campaignId,
      }
    : null;
  const appViewerContext = localOnlyLaunch ? null : (campaignCharViewerContext || viewerContext);
  const appKey = buildAuthedAppKey(serverVc, appViewerContext, launchTarget);

  if (!localOnlyLaunch && viewerContext === undefined) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px', color: '#e8dcc4', fontFamily: 'serif' }}>
        <div style={{ fontSize: '1.3rem' }}>Resolving viewer context…</div>
      </div>
    );
  }

  return (
    <App
      key={appKey}
      viewerContext={appViewerContext}
      viewerAuthToken={localOnlyLaunch ? null : authToken}
      authMovementAuthorityEnabled={!localOnlyLaunch && !!viewerContext && !testVc}
      claimViewedCharAsOwner={localOnlyLaunch ? null : claimCharAsOwner}
      useServerDerivedViewerQueries={!localOnlyLaunch && !testVc && !!viewerContext}
      forceLocalStorage={localOnlyLaunch}
      characterImportUploadClient={!localOnlyLaunch ? characterImportUploadClient : null}
      createStandaloneCharacterFromImport={!localOnlyLaunch ? createStandaloneCharacterFromImport : null}
      onCharacterImportCreateApplied={!localOnlyLaunch ? onCharacterImportCreateApplied : null}
      initialLaunch={syntheticDemoLaunch
        ? buildSyntheticDemoInitialLaunch(launchTarget)
        : (campaignSandboxLaunch
          ? buildCampaignSandboxInitialLaunch(launchTarget)
          : buildAuthedInitialLaunch(launchTarget))}
      sandboxStorageSeed={campaignSandboxLaunch ? launchTarget.storageSeed : null}
      campaignRuntimeSettings={localOnlyLaunch ? null : liveCampaignRuntimeSettings}
      onReturnToCampaignLanding={!testVc ? () => setLaunchTarget(null) : null}
    />
  );
};

const AuthGate = ({ useConvexAuthHook, useAuthActions, useAuthToken, useMutation, useAction, useQuery, useConvex, api }) => {
  const { isLoading, isAuthenticated } = useConvexAuthHook();
  const { signIn, signOut } = useAuthActions();
  const authToken = useAuthToken();
  const [signingIn, setSigningIn] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Expose sign-out + auth state on window so DevTools can verify + recover.
  // Dev-only; production auth state should remain in the app UI/provider.
  React.useEffect(() => {
    return publishConvexAuthDebugGlobals({ signOut, isLoading, isAuthenticated });
  }, [signOut, isLoading, isAuthenticated]);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px', color: '#e8dcc4', fontFamily: 'serif' }}>
        <div style={{ fontSize: '1.3rem' }}>Checking sign-in status…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px', color: '#e8dcc4', fontFamily: 'serif', maxWidth: '460px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '24px', color: '#c9a227' }}>⚔️ Survival Tracker</h1>
        {/* Maintenance notice for signed-out visitors. Sign-in stays
            available — that's the maintainer's path back in; non-admin
            accounts hit the full maintenance screen after sign-in. */}
        <SignedOutMaintenanceNotice useQuery={useQuery} api={api} />
        <p style={{ marginBottom: '32px', lineHeight: 1.5 }}>
          Sign in with Google to continue. Your session persists across reloads.
        </p>
        <button
          disabled={signingIn}
          onClick={async () => {
            setSigningIn(true);
            setError(null);
            try {
              await signIn('google');
            } catch (e) {
              console.error('signIn failed', e);
              setError(e?.message || 'Sign-in failed');
              setSigningIn(false);
            }
          }}
          style={{
            padding: '12px 24px',
            fontSize: '1rem',
            background: '#c9a227',
            color: '#2a1a0a',
            border: 'none',
            borderRadius: '4px',
            cursor: signingIn ? 'wait' : 'pointer',
            opacity: signingIn ? 0.6 : 1,
            fontWeight: 'bold',
          }}
        >
          {signingIn ? 'Redirecting to Google…' : '🔑 Sign in with Google'}
        </button>
        {error && <div style={{ marginTop: '20px', color: '#e86060' }}>{error}</div>}
        {/* Dev access affordance — only visible on localhost (when
            unauthenticated) or to the dev allowlist after sign-in. The
            component handles its own visibility; clicking from the
            unauthenticated state simply nudges the user to sign in
            since the dev dashboard requires auth to read. */}
        <DevAccessLink
          onNavigate={() => {
            if (typeof window !== 'undefined') {
              window.alert('Sign in first to view dev reports.');
            }
          }}
        />
        {shouldShowAuthRollbackHint() && (
          <div style={{ marginTop: '48px', fontSize: '0.8rem', opacity: 0.6 }}>
            To roll back to demo-auth, run in DevTools:{' '}
            <code>window.__disableConvexAuth__()</code>
          </div>
        )}
      </div>
    );
  }

  return <AuthedApp useQuery={useQuery} useMutation={useMutation} useAction={useAction} useConvex={useConvex} api={api} authToken={authToken} signOut={signOut} />;
};

const AuthRoot = () => {
  const [ready, setReady] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const url = process.env.REACT_APP_CONVEX_URL;
      if (!url) {
        console.error('REACT_APP_CONVEX_URL is not set; falling back to demo-auth.');
        if (!cancelled) setReady({ mode: 'fallback' });
        return;
      }
      const [
        { ConvexReactClient, useConvexAuth: useConvexAuthHook, useMutation, useAction, useQuery, useConvex },
        { ConvexAuthProvider, useAuthActions, useAuthToken },
        { api },
      ] = await Promise.all([
        import('convex/react'),
        import('@convex-dev/auth/react'),
        import('./convex/_generated/api'),
      ]);
      if (cancelled) return;
      setReady({
        mode: 'auth',
        client: new ConvexReactClient(url),
        ConvexAuthProvider,
        useConvexAuthHook,
        useMutation,
        useAction,
        useAuthActions,
        useAuthToken,
        useQuery,
        useConvex,
        api,
      });
    })();
    return () => { cancelled = true; };
  }, []);

  if (ready === null) return null;
  if (ready.mode === 'fallback') return <App />;

  const { ConvexAuthProvider, client, useConvexAuthHook, useAuthActions, useAuthToken, useMutation, useAction, useQuery, useConvex, api } = ready;
  return (
    <ConvexAuthProvider client={client}>
      <AuthGate
        useConvexAuthHook={useConvexAuthHook}
        useAuthActions={useAuthActions}
        useAuthToken={useAuthToken}
        useMutation={useMutation}
        useAction={useAction}
        useQuery={useQuery}
        useConvex={useConvex}
        api={api}
      />
    </ConvexAuthProvider>
  );
};

// Session 9 (2026-04-20): `?local=1` (and `window.__LOCAL_ONLY__`) short-circuit
// auth even when `useConvexAuth` is on. This is the Kickstarter demo path —
// one URL (no params) is the real auth-gated app, the same URL + `?local=1`
// is a self-contained in-memory demo with no sign-in required. Both live in
// the same Vercel deployment; the query param decides which face users see.
const Root = () => {
  React.useEffect(() => {
    const cleanupAuthFlags = installConvexAuthFlagGlobals();
    const cleanupTestViewer = installTestViewerContextGlobals();
    return () => {
      cleanupTestViewer();
      cleanupAuthFlags();
    };
  }, []);
  // `/character-builder` is a standalone scaffold route — short-circuits
  // both the auth flow and the demo flow. See srd/character-creation-plan.md.
  if (isCharacterBuilderRoute()) {
    return (
      <React.Suspense fallback={null}>
        <CharacterBuilderLazy />
      </React.Suspense>
    );
  }
  const sandboxSeed = typeof window !== 'undefined'
    ? buildCampaignSandboxSeedFromSearch(window.location.search)
    : null;
  if (sandboxSeed) {
    const returnToCampaignLanding = () => {
      if (typeof window === 'undefined') return;
      window.location.assign(window.location.pathname || '/');
    };
    return (
      <App
        forceLocalStorage
        initialLaunch={sandboxSeed.initialLaunch}
        sandboxStorageSeed={sandboxSeed.storageSeed}
        onReturnToCampaignLanding={returnToCampaignLanding}
      />
    );
  }
  const authOn = shouldRenderAuthRoot(FEATURE_FLAGS, shouldForceLocalMode());
  return authOn ? <AuthRoot /> : <App />;
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<Root />);
